import { Users, TrendingUp, Smartphone, DollarSign, Heart } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export function InsightsPage() {
  // Age Distribution
  const ageData = [
    { name: '18-24', value: 28, color: '#3b82f6' },
    { name: '25-34', value: 42, color: '#1d4ed8' },
    { name: '35-44', value: 18, color: '#60a5fa' },
    { name: '45-54', value: 8, color: '#93c5fd' },
    { name: '55+', value: 4, color: '#dbeafe' }
  ];

  // Gender Distribution
  const genderData = [
    { name: 'Female', value: 58, color: '#ec4899' },
    { name: 'Male', value: 39, color: '#3b82f6' },
    { name: 'Non-binary/Other', value: 3, color: '#8b5cf6' }
  ];

  // Income Levels
  const incomeData = [
    { name: 'Under $30k', value: 15, color: '#dbeafe' },
    { name: '$30k-$50k', value: 22, color: '#93c5fd' },
    { name: '$50k-$75k', value: 28, color: '#60a5fa' },
    { name: '$75k-$100k', value: 20, color: '#3b82f6' },
    { name: 'Over $100k', value: 15, color: '#1d4ed8' }
  ];

  // Platform Usage
  const platformData = [
    { platform: 'Instagram', daily: 78, weekly: 15, monthly: 5, rarely: 2 },
    { platform: 'TikTok', daily: 65, weekly: 22, monthly: 8, rarely: 5 },
    { platform: 'YouTube', daily: 52, weekly: 30, monthly: 12, rarely: 6 },
    { platform: 'Facebook', daily: 35, weekly: 25, monthly: 20, rarely: 20 },
    { platform: 'Twitter/X', daily: 28, weekly: 18, monthly: 15, rarely: 39 },
    { platform: 'Pinterest', daily: 18, weekly: 22, monthly: 25, rarely: 35 }
  ];

  // Purchase Behavior
  const purchaseInfluenceData = [
    { factor: 'Authentic Reviews', percentage: 82 },
    { factor: 'Creator Recommendation', percentage: 71 },
    { factor: 'Before/After Results', percentage: 68 },
    { factor: 'Discount Codes', percentage: 64 },
    { factor: 'Tutorial/How-to', percentage: 59 },
    { factor: 'Celebrity Endorsement', percentage: 34 }
  ];

  // Content Preferences
  const contentTypeData = [
    { type: 'Product Reviews', interest: 85 },
    { type: 'Tutorials/How-to', interest: 78 },
    { type: 'Behind-the-Scenes', interest: 72 },
    { type: 'Day in the Life', interest: 68 },
    { type: 'Unboxing', interest: 61 },
    { type: 'Product Comparisons', interest: 74 }
  ];

  // Shopping Categories
  const shoppingCategoryData = [
    { name: 'Beauty/Skincare', value: 68, color: '#ec4899' },
    { name: 'Fashion/Apparel', value: 62, color: '#8b5cf6' },
    { name: 'Tech/Electronics', value: 45, color: '#3b82f6' },
    { name: 'Home/Lifestyle', value: 52, color: '#10b981' },
    { name: 'Food/Beverage', value: 41, color: '#f59e0b' },
    { name: 'Fitness/Wellness', value: 55, color: '#06b6d4' }
  ];

  // Trust Levels
  const trustData = [
    { name: 'Trust Completely', value: 12, color: '#10b981' },
    { name: 'Trust Somewhat', value: 48, color: '#3b82f6' },
    { name: 'Neutral', value: 28, color: '#8b5cf6' },
    { name: 'Skeptical', value: 10, color: '#f59e0b' },
    { name: 'Very Skeptical', value: 2, color: '#ef4444' }
  ];

  const _COLORS = ['#3b82f6', '#1d4ed8', '#60a5fa', '#93c5fd', '#dbeafe'];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="max-w-[1400px] mx-auto p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-gray-900 mb-2">Audience Insights</h1>
              <p className="text-gray-600">
                Comprehensive analysis of your target audience demographics, behaviors, and preferences
              </p>
            </div>
            <Badge className="bg-green-100 text-green-700 border-green-300 border">
              Based on 5,247 responses
            </Badge>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-xs text-gray-500">Primary Age</span>
              </div>
              <div className="text-2xl text-gray-900 mb-1">25-34</div>
              <div className="text-xs text-gray-600">42% of audience</div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Smartphone className="w-5 h-5 text-pink-600" />
                <span className="text-xs text-gray-500">Top Platform</span>
              </div>
              <div className="text-2xl text-gray-900 mb-1">Instagram</div>
              <div className="text-xs text-gray-600">78% daily active</div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-xs text-gray-500">Avg Income</span>
              </div>
              <div className="text-2xl text-gray-900 mb-1">$50-75K</div>
              <div className="text-xs text-gray-600">28% bracket</div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-5 h-5 text-red-600" />
                <span className="text-xs text-gray-500">Trust Level</span>
              </div>
              <div className="text-2xl text-gray-900 mb-1">60%</div>
              <div className="text-xs text-gray-600">Trust influencers</div>
            </CardContent>
          </Card>
        </div>

        {/* Demographics Section */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Age Distribution */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Age Distribution</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={ageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {ageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {ageData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                    <span className="text-gray-900">{item.value}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Gender Distribution */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Gender Distribution</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name: _name, value }) => `${value}%`}
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {genderData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                    <span className="text-gray-900">{item.value}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Income Distribution */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Income Levels</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={incomeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    label={({ value }) => `${value}%`}
                  >
                    {incomeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {incomeData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                    <span className="text-gray-900">{item.value}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Usage */}
        <Card className="border-0 shadow-sm mb-6">
          <CardContent className="p-6">
            <h3 className="text-gray-900 mb-4">Social Media Platform Usage</h3>
            <p className="text-sm text-gray-600 mb-6">Frequency of platform engagement by your target audience</p>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={platformData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#9ca3af" />
                <YAxis dataKey="platform" type="category" stroke="#9ca3af" width={80} />
                <Tooltip />
                <Legend />
                <Bar dataKey="daily" stackId="a" fill="#3b82f6" name="Daily" />
                <Bar dataKey="weekly" stackId="a" fill="#60a5fa" name="Weekly" />
                <Bar dataKey="monthly" stackId="a" fill="#93c5fd" name="Monthly" />
                <Bar dataKey="rarely" stackId="a" fill="#dbeafe" name="Rarely" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Purchase Behavior */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">What Influences Purchase Decisions</h3>
              <p className="text-sm text-gray-600 mb-6">Top factors that drive audience to buy</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={purchaseInfluenceData} layout="vertical" margin={{ left: 140 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="factor" type="category" stroke="#9ca3af" width={140} />
                  <Tooltip />
                  <Bar dataKey="percentage" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Preferred Content Types</h3>
              <p className="text-sm text-gray-600 mb-6">Content formats with highest engagement</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={contentTypeData} layout="vertical" margin={{ left: 140 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="type" type="category" stroke="#9ca3af" width={140} />
                  <Tooltip />
                  <Bar dataKey="interest" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Shopping Categories & Trust */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Top Shopping Categories</h3>
              <p className="text-sm text-gray-600 mb-6">Product categories most purchased from influencer recommendations</p>
              <div className="space-y-4">
                {shoppingCategoryData.map((item) => (
                  <div key={item.name} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{item.name}</span>
                      <span className="text-gray-900">{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${item.value}%`, backgroundColor: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-gray-900 mb-4">Trust in Influencer Recommendations</h3>
              <p className="text-sm text-gray-600 mb-6">How much audiences trust creator product recommendations</p>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={trustData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name: _name, value }) => `${value}%`}
                  >
                    {trustData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {trustData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-gray-700">{item.name}</span>
                    </div>
                    <span className="text-gray-900">{item.value}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Key Insights */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-600 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 mb-3">Key Takeaways</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Primary Audience:</strong> 25-34 year old females with $50-75K income, highly active on Instagram
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Trust Factor:</strong> 60% trust influencer recommendations, with authentic reviews being the #1 driver
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Platform Strategy:</strong> Focus on Instagram (78% daily) and TikTok (65% daily) for maximum reach
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Content Priority:</strong> Product reviews (85%) and tutorials (78%) generate highest interest
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Top Categories:</strong> Beauty/skincare (68%) and fashion (62%) lead in influencer-driven purchases
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></div>
                      <p className="text-sm text-gray-700">
                        <strong>Conversion Drivers:</strong> Authentic reviews, creator recommendations, and visible results trump discounts
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}